import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

const products = [
  {
    id: "scrunchie",
    name: "The Everyday Scrunchie",
    price: "$5",
    img: "/photos/scrunchie-main.webp",
    alt: "Handmade gray crochet scrunchie with pink speckles, made by Els",
    altImg: "/photos/scrunchie-green-purple.webp",
    altImgAlt: "Finished green and purple crochet scrunchie, made by Els",
    altNote: "also in green & purple!",
    blurb: "Squishy, stretchy and gentle on your hair. The little pop of cozy every ponytail deserves.",
    note: "hair's new best friend",
    orderValue: "Scrunchie ($5)",
  },
  {
    id: "bandana",
    name: "The Sunny Day Bandana",
    price: "$10",
    img: "/photos/bandana.jpeg",
    alt: "Handmade pink and blue crochet bandana, made by Els",
    blurb: "A granny-stitch triangle scarf for market mornings, garden days and golden-hour walks.",
    note: "instant cottage-core",
    orderValue: "Bandana ($10)",
  },
  {
    id: "earwarmer",
    name: "The Toasty Ear Warmer",
    price: "$10",
    img: "/photos/earwarmers.webp",
    alt: "Fuzzy handmade crochet ear warmers in sage green and pink, made by Els",
    blurb: "Cloud-soft fuzzy yarn that keeps ears snuggly on chilly walks. Made to order in your favorite colors — just ask and Els will stitch one up for you!",
    note: "custom colors welcome",
    orderValue: "Ear Warmer ($10)",
  },
];

const Products = ({ onOrder }) => {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="shop" className="py-28 sm:py-36" data-testid="products-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="font-hand text-2xl text-[#A67C52] -rotate-2 mb-2">the little shop</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl sm:text-4xl text-[#2C402B]">
            Three treasures, made by hand
          </h2>
          <p className="mt-4 text-base text-[#576956] max-w-lg leading-relaxed">
            Every piece is crocheted by Els herself, so each one is a tiny bit
            one-of-a-kind — just like you. Ear warmers can even be made custom
            in your favorite colors. Tap any photo for a closer look.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8">
          {products.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className={`group ${i === 1 ? "md:mt-16" : ""}`}
              data-testid={`product-${p.id}-card`}
            >
              <div className="relative">
                <button
                  onClick={() => setLightbox({ src: p.img, alt: p.alt, label: `${p.name} — ${p.price}` })}
                  className="block w-full rounded-t-[999px] rounded-b-[2rem] overflow-hidden border border-[#D3D7CF] shadow-[0_8px_30px_rgba(44,64,43,0.08)] cursor-zoom-in relative"
                  data-testid={`product-${p.id}-image-button`}
                  aria-label={`View larger photo of ${p.name}`}
                >
                  <img
                    src={p.img}
                    alt={p.alt}
                    className="w-full h-80 sm:h-96 object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    data-testid={`product-${p.id}-image`}
                  />
                  <span className="absolute bottom-4 right-4 bg-[#F9F6F0]/85 backdrop-blur-md rounded-full p-2.5 text-[#2C402B] opacity-0 translate-y-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                    <ZoomIn size={18} />
                  </span>
                </button>
                <div
                  className="absolute -top-3 -right-2 sm:-right-4 bg-[#C27F67] text-[#F9F6F0] rounded-full w-20 h-20 flex items-center justify-center rotate-6 shadow-[0_8px_20px_rgba(194,127,103,0.4)] transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110 z-10 pointer-events-none"
                  data-testid={`product-${p.id}-price`}
                >
                  <span className="font-hand text-3xl font-semibold">{p.price}</span>
                </div>
                {p.altImg && (
                  <div className="absolute -bottom-8 -left-3 sm:-left-6 z-10">
                    <button
                      onClick={() => setLightbox({ src: p.altImg, alt: p.altImgAlt, label: `${p.name} — green & purple` })}
                      className="block w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden border-4 border-white shadow-[0_10px_25px_rgba(44,64,43,0.2)] -rotate-6 transition-transform duration-300 hover:-rotate-2 hover:scale-105 cursor-zoom-in"
                      data-testid={`product-${p.id}-alt-image-button`}
                      aria-label={`View larger photo of ${p.name} in green and purple`}
                    >
                      <img
                        src={p.altImg}
                        alt={p.altImgAlt}
                        className="w-full h-full object-cover"
                        data-testid={`product-${p.id}-alt-image`}
                      />
                    </button>
                    <p className="font-hand text-lg text-[#A67C52] -rotate-3 mt-1.5 ml-1">{p.altNote}</p>
                  </div>
                )}
              </div>
              <div className={`mt-7 px-1 ${p.altImg ? "pt-8" : ""}`}>
                <p className="font-hand text-xl text-[#A67C52] -rotate-1">{p.note}</p>
                <h3 className="font-heading font-medium text-2xl text-[#2C402B] mt-1">{p.name}</h3>
                <p className="mt-3 text-[#576956] leading-relaxed text-sm sm:text-base">{p.blurb}</p>
                <button
                  onClick={() => onOrder(p.orderValue)}
                  data-testid={`product-${p.id}-order-button`}
                  className="mt-6 rounded-full border-2 border-[#2C402B] text-[#2C402B] px-6 py-2.5 font-heading font-medium text-sm transition-[background-color,color,transform] duration-300 hover:bg-[#2C402B] hover:text-[#F9F6F0] hover:scale-105"
                >
                  I want this one
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-[#2C402B]/70 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-overlay"
          >
            <motion.figure
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="relative max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.alt}
                className="w-full max-h-[75vh] object-contain rounded-3xl border-4 border-white shadow-[0_30px_80px_rgba(0,0,0,0.4)] bg-white"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-4 text-center font-hand text-2xl text-[#F9F6F0]" data-testid="lightbox-caption">
                {lightbox.label}
              </figcaption>
              <button
                onClick={() => setLightbox(null)}
                className="absolute -top-4 -right-4 bg-[#C27F67] text-[#F9F6F0] rounded-full p-3 shadow-lg transition-[transform,background-color] duration-300 hover:bg-[#2C402B] hover:scale-110"
                data-testid="lightbox-close-button"
                aria-label="Close photo"
              >
                <X size={20} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Products;
